import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { getEventos } from '../api';
import { isEventoClosed } from '../utils/eventoStatus';
import EventoCard from '../components/EventoCard';

export default function Home() {
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [periodo, setPeriodo] = useState('todos');

  useEffect(() => {
    getEventos().then(data => {
      setEventos(data);
      setLoading(false);
    }).catch(err => {
      console.error(err);
      setLoading(false);
    });
  }, []);

  const proximosAbertos = eventos
    .filter(evt => !isEventoClosed(evt))
    .sort((a, b) => new Date(a.data_evento) - new Date(b.data_evento));

  const proximos = proximosAbertos
    .filter(evt => {
      if (!busca) return true;
      const termo = busca.toLowerCase();
      return evt.titulo.toLowerCase().includes(termo) || (evt.descricao || '').toLowerCase().includes(termo);
    })
    .filter(evt => {
      if (periodo === 'todos' || !evt.data_evento) return true;
      const dias = (new Date(evt.data_evento) - new Date()) / (1000 * 60 * 60 * 24);
      if (periodo === 'semana') return dias <= 7;
      if (periodo === 'mes') return dias <= 30;
      return true;
    });

  const destaques = eventos.filter(evt => evt.destaque && !isEventoClosed(evt));

  return (
    <div className="flex-grow">

      {destaques.length > 0 && (
        <Swiper
          modules={[Autoplay, Pagination, Navigation]}
          spaceBetween={0}
          centeredSlides
          loop={destaques.length > 1}
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          pagination={{ clickable: true }}
          navigation
          className="heroSwiper w-full h-[60vh] min-h-[450px]"
        >
          {destaques.map(evento => {
            const semLoteAberto = evento.preco === null || evento.preco === undefined || evento.preco === '';
            return (
              <SwiperSlide key={evento.id} className="relative">
                <img
                  src={evento.imagem_url || 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=1920&auto=format&fit=crop'}
                  alt={evento.titulo}
                  width={1920}
                  height={1080}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/70 to-transparent"></div>
                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-center items-start">
                  <span className="inline-block py-1 px-3 rounded-full bg-red-500/20 text-red-400 text-xs font-bold uppercase tracking-wider mb-4 border border-red-500/30 backdrop-blur-sm">Destaque</span>
                  <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4 tracking-tight max-w-2xl">{evento.titulo}</h2>
                  <p className="text-lg text-gray-300 max-w-xl mb-8 font-light line-clamp-3">{evento.descricao}</p>
                  {semLoteAberto ? (
                    <button disabled className="bg-white/10 text-gray-300 border border-white/20 px-8 py-3 rounded-lg font-semibold cursor-not-allowed">
                      Em Breve
                    </button>
                  ) : (
                    <Link
                      to={`/eventos/${evento.slug}`}
                      className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-lg font-semibold transition-all flex items-center gap-2"
                    >
                      Ver Detalhes <i className="fas fa-arrow-right text-sm"></i>
                    </Link>
                  )}
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      )}

      <div className="bg-dark pt-10 pb-16 md:pt-14 md:pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-white text-2xl md:text-3xl font-extrabold tracking-tight">Encontre seu próximo evento</h2>
          <p className="text-primary-100 text-sm md:text-base mt-2">Pesquise por nome ou filtre por período</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 md:-mt-12 relative z-10">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-3 md:p-4">
          <div className="flex items-center gap-2 rounded-full border border-gray-200 px-4 py-3">
            <Search size={18} className="text-primary-600 flex-shrink-0" />
            <input
              type="text"
              placeholder="Pesquisar eventos..."
              value={busca}
              onChange={e => setBusca(e.target.value)}
              className="w-full text-sm text-gray-700 outline-none"
            />
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <label htmlFor="periodo-select" className="sr-only">Filtrar por período</label>
            <select
              id="periodo-select"
              value={periodo}
              onChange={e => setPeriodo(e.target.value)}
              className="rounded-full border border-gray-200 px-4 py-2 text-sm text-gray-600 bg-white outline-none"
            >
              <option value="todos">Qualquer data</option>
              <option value="semana">Esta semana</option>
              <option value="mes">Este mês</option>
            </select>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="mb-8">
          <h3 className="text-2xl font-bold text-gray-900 tracking-tight">Próximos Eventos</h3>
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-400">Carregando eventos...</div>
        ) : proximosAbertos.length === 0 ? (
          <div className="max-w-md mx-auto glass-card rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="h-52 bg-gray-50 flex items-center justify-center border-b border-gray-100">
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-gray-100 text-gray-400 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
                  <i className="fas fa-calendar-alt"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-800">Novas Inscrições</h3>
                <span className="inline-block mt-2 bg-gray-200 text-gray-600 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md">Em Breve</span>
              </div>
            </div>
            <div className="p-6 bg-white text-center">
              <p className="text-gray-500 text-sm mb-6">Nenhum evento com inscrições abertas no momento. Fique atento, em breve teremos novidades por aqui.</p>
              <button disabled className="w-full bg-gray-100 text-gray-400 px-5 py-2.5 rounded-lg text-sm font-semibold cursor-not-allowed">
                Aguarde Abertura
              </button>
            </div>
          </div>
        ) : proximos.length === 0 ? (
          <div className="text-center py-16 text-gray-400">Nenhum evento encontrado com esse filtro.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {proximos.map(evento => (
              <EventoCard key={evento.id} evento={evento} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
